"use client";

import { type TenantContextValue, useTenant } from "@tillix/tenants/react";
import { type BaseCardProps, baseCardKit } from "./card";
import { conEdisonCardKit } from "./card.con-edison";
import type { OptionalTenantProp } from "../../../types";

export interface CardParts {
  Root: React.FC<BaseCardProps>;
  Header: React.FC<BaseCardProps>;
  Title: React.FC<BaseCardProps>;
  Description: React.FC<BaseCardProps>;
  Content: React.FC<BaseCardProps>;
  Footer: React.FC<BaseCardProps>;
}

function useCardKit(tenantProp?: TenantContextValue) {
  const contextTenant = useTenant();
  const tenantRefId = tenantProp?.id ?? contextTenant?.id;

  switch (tenantRefId) {
    case "con-edison":
      return conEdisonCardKit;
    default:
      return baseCardKit;
  }
}

function makeCardPart<P extends object>(pick: (kit: CardParts) => React.FC<P>) {
  return function CardPart(props: P & OptionalTenantProp) {
    const { tenant, ...rest } = props;
    const Kit = useCardKit(tenant);
    const Comp = pick(Kit);
    return <Comp {...(rest as P)} />;
  };
}

export const Card = makeCardPart<BaseCardProps>(
  (k) => k.Root,
) as React.FC<BaseCardProps> & {
  Header: React.FC<BaseCardProps>;
  Title: React.FC<BaseCardProps>;
  Description: React.FC<BaseCardProps>;
  Content: React.FC<BaseCardProps>;
  Footer: React.FC<BaseCardProps>;
};

Card.Header = makeCardPart<BaseCardProps>((k) => k.Header);
Card.Title = makeCardPart<BaseCardProps>((k) => k.Title);
Card.Description = makeCardPart<BaseCardProps>((k) => k.Description);
Card.Content = makeCardPart<BaseCardProps>((k) => k.Content);
Card.Footer = makeCardPart<BaseCardProps>((k) => k.Footer);
